import React, { useState, useEffect, Suspense, lazy } from 'react';
import {
    BookOpen,
    PenTool,
    Video,
    Mic,
    Mail,
    Github,
    Twitter,
    Linkedin,
    ArrowUpRight,
    Users,
    Clock,
    Copy,
    Check,
    Sun,
    Moon
} from 'lucide-react';

// 路由页面全部按需懒加载，首屏只加载首页本身，加快首次访问速度
const GameSnake = lazy(() => import('./game/GameSnake'));
const GameClaw = lazy(() => import('./game/GameClaw'));
const GameBowling = lazy(() => import('./game/GameBowling'));
const GameFlightChess = lazy(() => import('./game/GameFlightChess'));
const GameHome = lazy(() => import('./game/GameHome'));
const StudyHome = lazy(() => import('./study/StudyHome'));
const EnglishHome = lazy(() => import('./study/english/EnglishHome'));
const ChineseHome = lazy(() => import('./study/chinese/ChineseHome'));
const PoetryHome = lazy(() => import('./study/chinese/poetry/PoetryHome'));
const ChineseDictation = lazy(() => import('./study/chinese/dictation/Dictation.jsx'));
const ProgramHome = lazy(() => import('./study/program/ProgramHome'));
const LovePage = lazy(() => import('./study/program/LovePage'));
const CppPracticePage = lazy(() => import('./study/program/CppPracticePage'));
const KnowledgeMapHome = lazy(() => import('./study/program/KnowledgeMapHome'));
const KnowledgeMapViewer = lazy(() => import('./study/program/KnowledgeMapViewer'));
const MathHome = lazy(() => import('./study/math/MathHome.jsx'));
const OralArithmetic = lazy(() => import('./study/math/OralArithmetic.jsx'));
const About = lazy(() => import('./about/About'));
const Wish2026 = lazy(() => import('./others/Wish2026'));
const AiToolkit = lazy(() => import('./ai/AiToolkit'));
// GESP 题库较大（约 1.4MB），按需懒加载，避免拖慢全站首屏
const GespHome = lazy(() => import('./study/program/gesp/GespHome'));
const ExamPage = lazy(() => import('./study/program/gesp/ExamPage'));
// OCR 听写页依赖 tesseract.js（体积大），按需加载
const Listen = lazy(() => import('./study/english/Listen.jsx'));

/**
 * 页面按需加载时的通用占位提示
 */
const PageFallback = () => (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center text-slate-500">
            <div className="inline-block h-6 w-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mb-3" />
            <p className="text-sm">正在加载...</p>
        </div>
    </div>
);

/**
 * GESP 题库按需加载时的占位提示（加载内容大，单独文案）
 */
const GespFallback = () => (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center text-slate-500">
            <div className="inline-block h-6 w-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mb-3" />
            <p className="text-sm">正在加载试卷...</p>
        </div>
    </div>
);

/**
 * 功能：站点主页面组件（主页模块入口版）
 * 说明：在根路径渲染学习/游戏模块入口；其他路径保持原路由逻辑
 */
const App = () => {
    const [isDark, setIsDark] = useState(true);
    const [copied, setCopied] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        // 简单的本地存储记忆
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
            setIsDark(savedTheme === 'dark');
        }
    }, []);

    const toggleTheme = () => {
        const newTheme = !isDark;
        setIsDark(newTheme);
        localStorage.setItem('theme', newTheme ? 'dark' : 'light');
    };

    const handleCopyEmail = () => {
        navigator.clipboard.writeText("teacher@example.com");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // 主题样式配置
    const theme = {
        bg: isDark ? 'bg-zinc-950' : 'bg-[#fdfbf7]', // 浅色模式使用暖调米白
        text: isDark ? 'text-zinc-200' : 'text-stone-800',
        subText: isDark ? 'text-zinc-400' : 'text-stone-500',
        border: isDark ? 'border-zinc-900' : 'border-stone-200',
        cardBg: isDark ? 'bg-zinc-900/30' : 'bg-white',
        cardBorder: isDark ? 'border-zinc-800/60' : 'border-stone-200',
        hoverBg: isDark ? 'hover:bg-zinc-900' : 'hover:bg-stone-50',
        accent: isDark ? 'text-indigo-400' : 'text-indigo-600',
        accentBg: isDark ? 'bg-indigo-500' : 'bg-indigo-600',
        tagBg: isDark ? 'bg-zinc-900' : 'bg-stone-100',
        socialBg: isDark ? 'bg-zinc-900/50' : 'bg-stone-100',
    };

    // 简易路由：当路径为 /game/*、/study 或 /study/english/listen 时渲染对应页面
    const path = typeof window !== 'undefined' ? window.location.pathname : '/'
    if (path === '/game' || path === '/game/') {
        return <Suspense fallback={<PageFallback />}><GameHome /></Suspense>
    }
    if (path === '/study' || path === '/study/') {
        return <Suspense fallback={<PageFallback />}><StudyHome /></Suspense>
    }
    if (path === '/study/english' || path === '/study/english/') {
        return <Suspense fallback={<PageFallback />}><EnglishHome /></Suspense>
    }
    if (path === '/study/chinese' || path === '/study/chinese/') {
        return <Suspense fallback={<PageFallback />}><ChineseHome /></Suspense>
    }
    if (path === '/study/chinese/poetry' || path === '/study/chinese/poetry/') {
        return <Suspense fallback={<PageFallback />}><PoetryHome /></Suspense>
    }
    if (path === '/study/chinese/dictation' || path === '/study/chinese/dictation/') {
        return <Suspense fallback={<PageFallback />}><ChineseDictation /></Suspense>
    }
    if (path === '/study/math' || path === '/study/math/') {
        return <Suspense fallback={<PageFallback />}><MathHome /></Suspense>
    }
    if (path === '/study/math/oral' || path === '/study/math/oral/') {
        return <Suspense fallback={<PageFallback />}><OralArithmetic /></Suspense>
    }
    if (path === '/study/program' || path === '/study/program/') {
        return <Suspense fallback={<PageFallback />}><ProgramHome /></Suspense>
    }
    if (path === '/study/program/love' || path === '/study/program/love/') {
        return <Suspense fallback={<PageFallback />}><LovePage /></Suspense>
    }
    if (path === '/study/program/cpp-practice' || path === '/study/program/cpp-practice/') {
        return <Suspense fallback={<PageFallback />}><CppPracticePage /></Suspense>
    }
    if (path === '/study/program/knowledge-map' || path === '/study/program/knowledge-map/') {
        return <Suspense fallback={<PageFallback />}><KnowledgeMapHome /></Suspense>
    }
    if (/^\/study\/program\/knowledge-map\/gesp-cpp-level[1-4]\/?$/.test(path)) {
        return <Suspense fallback={<PageFallback />}><KnowledgeMapViewer /></Suspense>
    }
    if (path === '/study/program/gesp' || path === '/study/program/gesp/') {
        return <Suspense fallback={<GespFallback />}><GespHome /></Suspense>
    }
    if (path.startsWith('/study/program/gesp/')) {
        return <Suspense fallback={<GespFallback />}><ExamPage /></Suspense>
    }
    if (path === '/game/1') {
        return <Suspense fallback={<PageFallback />}><GameSnake /></Suspense>
    }
    if (path === '/game/2') {
        return <Suspense fallback={<PageFallback />}><GameClaw /></Suspense>
    }
    if (path === '/game/3') {
        return <Suspense fallback={<PageFallback />}><GameBowling /></Suspense>
    }
    if (path === '/game/4') {
        return <Suspense fallback={<PageFallback />}><GameFlightChess /></Suspense>
    }
    if (path.startsWith('/study/english/listen')) {
        return <Suspense fallback={<PageFallback />}><Listen /></Suspense>
    }
    if (path === '/about' || path === '/about/') {
        return <Suspense fallback={<PageFallback />}><About /></Suspense>
    }
    if (path === '/wish2026' || path === '/wish2026/') {
        return <Suspense fallback={<PageFallback />}><Wish2026 /></Suspense>
    }
    if (path === '/ai' || path.startsWith('/ai/')) {
        return <Suspense fallback={<PageFallback />}><AiToolkit /></Suspense>
    }
    if (path === '/' || path === '') {
        return <PortalHome isDark={isDark} theme={theme} onToggleTheme={toggleTheme} />
    }

    return (
        <div className={`min-h-screen ${theme.bg} ${theme.text} font-sans selection:bg-indigo-500/30 selection:text-indigo-600 transition-colors duration-500 ease-in-out ${mounted ? 'opacity-100' : 'opacity-0'}`}>

            {/* 顶部导航 / Header */}
            <header className="max-w-4xl mx-auto px-6 py-12 md:py-20 flex flex-col md:flex-row md:justify-between md:items-start gap-8 relative">

                {/* 主题切换按钮 (绝对定位在移动端右上角，桌面端跟随布局) */}
                <button
                    onClick={toggleTheme}
                    className={`absolute top-6 right-6 md:static md:order-last p-3 rounded-full transition-all duration-300 ${isDark ? 'bg-zinc-900 text-yellow-400 hover:bg-zinc-800' : 'bg-stone-100 text-stone-600 hover:bg-stone-200 shadow-sm'}`}
                    aria-label="Toggle Theme"
                >
                    {isDark ? <Sun size={20} /> : <Moon size={20} />}
                </button>

                <div className="space-y-6 flex-1">
                    {/* 头像区域 */}
                    <div className={`relative w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden ring-2 shadow-xl group transition-colors duration-500 ${isDark ? 'bg-zinc-800 ring-zinc-800' : 'bg-white ring-stone-200'}`}>
                        <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br transition-colors duration-500 ${isDark ? 'from-indigo-900 to-zinc-900 text-indigo-200' : 'from-indigo-100 to-white text-indigo-600'}`}>
                            {/* 头像使用教育题材图标（BookOpen），与 favicon 统一 */}
                            <BookOpen size={28} className={isDark ? 'text-indigo-200' : 'text-indigo-600'} />
                        </div>
                        <div className="absolute inset-0 bg-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>

                    <div>
                        <h1 className={`text-3xl md:text-4xl font-bold tracking-tight transition-colors duration-500 ${isDark ? 'text-zinc-100' : 'text-stone-900'}`}>
                            Hi, I'm yuanniuniu.
                        </h1>
                        <p className={`mt-3 text-lg max-w-md leading-relaxed transition-colors duration-500 ${theme.subText}`}>
                            独立教育者，课程设计师，终身学习践行者。<br />
                            致力于将复杂的知识简单化，将枯燥的学习游戏化。
                        </p>

                        {/* 标签 Tagline */}
                        <div className="mt-4 flex flex-wrap gap-3 text-sm font-medium opacity-80">
                            {['STEM Education', 'EdTech Explorer', 'Writer'].map((tag) => (
                                <span key={tag} className={`px-2 py-1 rounded border transition-colors duration-500 cursor-default ${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-500 hover:border-zinc-700' : 'bg-white border-stone-200 text-stone-500 hover:border-stone-300 shadow-sm'}`}>
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* 社交链接 */}
                    <div className="flex gap-5 text-zinc-500">
                        <SocialLink href="#" icon={<Github size={20} />} label="Github" theme={theme} />
                        <SocialLink href="#" icon={<Twitter size={20} />} label="Twitter" theme={theme} />
                        <SocialLink href="#" icon={<Linkedin size={20} />} label="LinkedIn" theme={theme} />
                    </div>
                </div>

                <div className="hidden md:block text-right mt-20 md:mt-0">
                    <p className={`text-sm uppercase tracking-widest font-semibold transition-colors duration-500 ${isDark ? 'text-zinc-500' : 'text-stone-400'}`}>Available for</p>
                    <ul className={`mt-2 space-y-1 text-sm transition-colors duration-500 ${isDark ? 'text-zinc-400' : 'text-stone-600'}`}>
                        <li>• 课程定制开发</li>
                        <li>• 教师培训工作坊</li>
                        <li>• 教育产品咨询</li>
                    </ul>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-6 pb-24 space-y-24">

                {/* 板块 01: 教学成果 (Works) */}
                <section>
                    <SectionHeader number="01" title="教学成果" subtitle="Curriculum & Projects" theme={theme} isDark={isDark} />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <CourseCard
                            title="Python 零基础创意编程"
                            category="Video Course"
                            stats="2,400+ 学员"
                            description="摒弃枯燥的语法背诵，通过制作 10 个复古小游戏来掌握 Python 核心逻辑。适合 12-18 岁青少年。"
                            tags={['Python', 'GameDev', 'K12']}
                            icon={<PenTool className={isDark ? "text-amber-400" : "text-amber-600"} size={24} />}
                            theme={theme}
                            isDark={isDark}
                        />
                        <CourseCard
                            title="可视化历史：二战风云"
                            category="Interactive Web"
                            stats="4.9/5.0 评分"
                            description="基于 D3.js 构建的交互式历史地图，让学生通过时间轴拖拽亲历历史变迁。"
                            tags={['History', 'Visualization', 'Web']}
                            icon={<BookOpen className={isDark ? "text-indigo-400" : "text-indigo-600"} size={24} />}
                            theme={theme}
                            isDark={isDark}
                        />
                        <CourseCard
                            title="Notion 打造超级个体"
                            category="Workshop"
                            stats="15 场线下"
                            description="面向大学生的知识管理工作坊，教你如何构建第二大脑，提升学习效率。"
                            tags={['Productivity', 'Notion', 'LifeLong']}
                            icon={<Video className={isDark ? "text-rose-400" : "text-rose-600"} size={24} />}
                            theme={theme}
                            isDark={isDark}
                        />
                        <CourseCard
                            title="AI 时代的批判性思维"
                            category="E-Book"
                            stats="500+ 下载"
                            description="当 GPT 能回答所有问题时，我们该教孩子什么？一本给家长的教育指南。"
                            tags={['AI', 'Pedagogy', 'Writing']}
                            icon={<Mic className={isDark ? "text-emerald-400" : "text-emerald-600"} size={24} />}
                            theme={theme}
                            isDark={isDark}
                        />
                    </div>
                </section>

                {/* 板块 02: 教育洞察 (Thinking) */}
                <section>
                    <SectionHeader number="02" title="教育洞察" subtitle="Insights & Blog" theme={theme} isDark={isDark} />

                    <div className="space-y-8">
                        <BlogPost
                            date="2024-03-15"
                            title="为什么费曼学习法在 AI 时代依然有效？"
                            summary="在信息过载的时代，'输出倒逼输入'的能力比单纯的知识获取更重要。本文探讨如何利用 AI 作为费曼学习法的辅助对象。"
                            tags={['Learning Method', 'AI']}
                            reads="1,240 reads"
                            theme={theme}
                            isDark={isDark}
                        />
                        <BlogPost
                            date="2024-02-02"
                            title="游戏化教学的误区：不仅是积分排行榜"
                            summary="真正的游戏化不是给学生发糖果，而是设计即时反馈机制和心流体验。分享我在设计编程课时的三个失败案例。"
                            tags={['Gamification', 'Design']}
                            reads="890 reads"
                            theme={theme}
                            isDark={isDark}
                        />
                        <BlogPost
                            date="2024-01-10"
                            title="2024 教育技术趋势预测：回归本质"
                            summary="AR/VR 也许很酷，但黑板和粉笔也有其不可替代的魔力。技术应当是隐形的翅膀，而不是沉重的铠甲。"
                            tags={['EdTech', 'Trend']}
                            reads="2,100 reads"
                            theme={theme}
                            isDark={isDark}
                        />
                    </div>

                    <div className="mt-8">
                        <a href="#" className={`inline-flex items-center text-sm font-medium transition-colors group ${isDark ? 'text-zinc-400 hover:text-indigo-400' : 'text-stone-500 hover:text-indigo-600'}`}>
                            阅读更多文章
                            <ArrowUpRight size={16} className="ml-1 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                    </div>
                </section>

                {/* 板块 03: 联系 (Contact) */}
                <section>
                    <SectionHeader number="03" title="建立连接" subtitle="Get in Touch" theme={theme} isDark={isDark} />

                    <div className={`rounded-2xl p-8 md:p-12 text-center relative overflow-hidden border transition-colors duration-500 ${isDark ? 'bg-zinc-900/50 border-zinc-800/50' : 'bg-white border-stone-200 shadow-sm'}`}>
                        {/* 背景装饰 */}
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-amber-500"></div>

                        <h3 className={`text-2xl md:text-3xl font-bold mb-4 transition-colors ${isDark ? 'text-zinc-100' : 'text-stone-800'}`}>
                            有有趣的教育项目？
                        </h3>
                        <p className={`mb-8 max-w-lg mx-auto transition-colors ${theme.subText}`}>
                            无论是学校讲座邀约、课程设计咨询，还是只是想聊聊关于学习的困惑，都欢迎随时联系。
                        </p>

                        <div className="flex flex-col md:flex-row items-center justify-center gap-4">
                            <button
                                onClick={handleCopyEmail}
                                className={`group relative flex items-center gap-3 px-6 py-3 rounded-full font-medium hover:text-white transition-all duration-300 ${isDark ? 'bg-zinc-100 text-zinc-900 hover:bg-indigo-500' : 'bg-stone-900 text-stone-50 hover:bg-indigo-600 shadow-md'}`}
                            >
                                <Mail size={18} />
                                <span>teacher@example.com</span>
                                <span className="ml-2 opacity-50 group-hover:opacity-100 border-l border-current pl-3">
                                    {copied ? <Check size={16} /> : <Copy size={16} />}
                                </span>
                                {copied && (
                                    <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-zinc-800 text-white text-xs px-2 py-1 rounded opacity-100 animate-fade-in-up">
                                        Copied!
                                    </span>
                                )}
                            </button>

                            <a href="#" className={`px-6 py-3 rounded-full border transition-colors ${isDark ? 'border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-white' : 'border-stone-300 text-stone-600 hover:border-stone-400 hover:text-stone-900'}`}>
                                预约 15min 咨询
                            </a>
                        </div>
                    </div>
                </section>

            </main>

            <footer className={`max-w-4xl mx-auto px-6 py-12 border-t text-center md:text-left flex flex-col md:flex-row justify-between items-center gap-4 transition-colors duration-500 ${theme.border}`}>
                <p className={`text-sm transition-colors ${isDark ? 'text-zinc-600' : 'text-stone-400'}`}>
                    © 2025 yuanniuniu Edu. Built with React & Tailwind.
                </p>
                <div className={`flex gap-6 text-sm transition-colors ${isDark ? 'text-zinc-600' : 'text-stone-500'}`}>
                    <a href="#" className={`transition-colors ${isDark ? 'hover:text-zinc-400' : 'hover:text-stone-800'}`}>RSS</a>
                    <a href="#" className={`transition-colors ${isDark ? 'hover:text-zinc-400' : 'hover:text-stone-800'}`}>About</a>
                    <a href="#" className={`transition-colors ${isDark ? 'hover:text-zinc-400' : 'hover:text-stone-800'}`}>Uses</a>
                </div>
            </footer>
        </div>
    );
};

// --- 子组件 ---

const SectionHeader = ({ number, title, subtitle, theme, isDark }) => (
    <div className={`flex items-baseline gap-4 mb-10 border-b pb-4 transition-colors duration-500 ${theme.border}`}>
        <span className={`font-mono text-sm font-bold tracking-wider ${isDark ? 'text-indigo-500' : 'text-indigo-600'}`}>{number}.</span>
        <h2 className={`text-2xl font-bold transition-colors ${theme.text}`}>{title}</h2>
        <span className={`hidden md:inline-block text-sm font-medium px-2 py-0.5 rounded border transition-colors ${isDark ? 'text-zinc-600 bg-zinc-900/50 border-zinc-800/50' : 'text-stone-500 bg-stone-100 border-stone-200'}`}>
            {subtitle}
        </span>
    </div>
);

const SocialLink = ({ href, icon, label, theme }) => (
    <a
        href={href}
        aria-label={label}
        className={`p-2 rounded-lg transition-all duration-300 border border-transparent ${theme.socialBg} ${theme.hoverBg} hover:text-indigo-400 hover:border-current`}
    >
        {icon}
    </a>
);

const CourseCard = ({ title, category, stats, description, tags, icon, theme, isDark }) => (
    <div className={`group border rounded-xl p-6 transition-all duration-300 cursor-pointer flex flex-col h-full ${theme.cardBg} ${theme.cardBorder} ${theme.hoverBg} ${!isDark && 'hover:shadow-md hover:border-stone-300'}`}>
        <div className={`flex justify-between items-start mb-4`}>
            <div className={`p-3 rounded-lg border transition-all ${isDark ? 'bg-zinc-950 border-zinc-800' : 'bg-stone-50 border-stone-200'} group-hover:border-indigo-500/30 group-hover:shadow-[0_0_15px_rgba(99,102,241,0.1)]`}>
                {icon}
            </div>
            <div className="text-right">
                <div className={`text-xs font-mono uppercase tracking-wider mb-1 transition-colors ${isDark ? 'text-zinc-500' : 'text-stone-400'}`}>{category}</div>
                <div className={`flex items-center gap-1 text-xs font-medium transition-colors ${theme.accent}`}>
                    <Users size={12} />
                    {stats}
                </div>
            </div>
        </div>

        <h3 className={`text-xl font-bold mb-3 transition-colors ${theme.text} ${isDark ? 'group-hover:text-indigo-400' : 'group-hover:text-indigo-600'}`}>
            {title}
        </h3>

        <p className={`text-sm leading-relaxed mb-6 flex-grow transition-colors ${theme.subText}`}>
            {description}
        </p>

        <div className={`flex flex-wrap gap-2 mt-auto pt-4 border-t transition-colors ${isDark ? 'border-zinc-800/50' : 'border-stone-100'}`}>
            {tags.map((tag, i) => (
                <span key={i} className={`text-xs font-medium px-1.5 py-0.5 rounded transition-colors ${isDark ? 'text-zinc-500 bg-transparent' : 'text-stone-500 bg-stone-100'}`}>
                    #{tag}
                </span>
            ))}
        </div>
    </div>
);

const BlogPost = ({ date, title, summary, tags, reads, theme, isDark }) => (
    <article className={`group flex flex-col md:flex-row gap-2 md:gap-8 p-4 -mx-4 rounded-xl transition-colors cursor-pointer ${isDark ? 'hover:bg-zinc-900/30' : 'hover:bg-white hover:shadow-sm'}`}>
        <div className={`md:w-32 flex-shrink-0 flex flex-row md:flex-col gap-3 md:gap-1 items-center md:items-start text-sm font-mono border-b md:border-b-0 md:border-r md:pr-4 pb-2 md:pb-0 mb-2 md:mb-0 transition-colors ${isDark ? 'text-zinc-500 border-zinc-800' : 'text-stone-400 border-stone-200'}`}>
            <span>{date}</span>
            <span className={`hidden md:inline w-8 h-[1px] my-1 transition-colors ${isDark ? 'bg-zinc-800' : 'bg-stone-200'}`}></span>
            <span className="flex items-center gap-1 text-xs opacity-60">
                <Clock size={12} /> {reads}
            </span>
        </div>

        <div className="flex-grow space-y-2">
            <h3 className={`text-lg md:text-xl font-bold transition-colors ${theme.text} ${isDark ? 'group-hover:text-indigo-400' : 'group-hover:text-indigo-600'}`}>
                {title}
            </h3>
            <p className={`text-sm leading-relaxed line-clamp-2 md:line-clamp-none transition-colors ${theme.subText}`}>
                {summary}
            </p>
            <div className="flex gap-3 mt-2">
                {tags.map((tag, i) => (
                    <span key={i} className={`text-xs transition-colors ${isDark ? 'text-zinc-600 group-hover:text-zinc-500' : 'text-stone-400 group-hover:text-stone-500'}`}>
                        #{tag}
                    </span>
                ))}
            </div>
        </div>
    </article>
);

export default App;

/**
 * 首页模块入口渲染组件
 * 说明：展示两个主模块卡片，链接到 /study 与 /game；支持主题切换（深色/白色）
 */
function PortalHome({ isDark, theme, onToggleTheme }) {
    /**
     * 页面挂载时设置浏览器标题
     * 中文注释：进入首页模块入口时，动态设置 document.title
     */
    useEffect(() => {
        document.title = '圆妞妞教育'
    }, [])
    return (
        <div className={`min-h-screen ${theme.bg} ${theme.text} font-sans`}>
            {/* 头部：标题与主题切换 */}
            <header className="max-w-4xl mx-auto px-6 py-12 relative">
                {/* 主题切换按钮（深色/白色） */}
                <button
                    onClick={onToggleTheme}
                    className={`absolute top-6 right-6 p-3 rounded-full transition-all duration-300 ${isDark ? 'bg-zinc-900 text-yellow-400 hover:bg-zinc-800' : 'bg-stone-100 text-stone-600 hover:bg-stone-200 shadow-sm'}`}
                    aria-label="Toggle Theme"
                >
                    {isDark ? <Sun size={20} /> : <Moon size={20} />}
                </button>
                <h1 className="text-3xl font-bold">yuanniuniu </h1>
                <p className={`mt-2 text-sm ${theme.subText}`}>从学习到游戏，点击进入对应模块。</p>
            </header>
            <main className="max-w-4xl mx-auto px-6 pb-24">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <a href="/study" className={`group block rounded-xl border ${theme.cardBorder} ${theme.cardBg} p-5 ${theme.hoverBg} transition`}>
                        <div className="flex items-center gap-3">
                            <BookOpen className="w-6 h-6 text-indigo-600" />
                            <h2 className="text-lg font-bold">学习中心 Study</h2>
                        </div>
                        <p className={`mt-2 text-sm ${theme.subText}`}>英语听写（English Dictation）与数学频道（Math）。</p>
                        <div className="mt-3 text-xs opacity-60">入口：/study</div>
                    </a>

                    <a href="/game" className={`group block rounded-xl border ${theme.cardBorder} ${theme.cardBg} p-5 ${theme.hoverBg} transition`}>
                        <div className="flex items-center gap-3">
                            <BookOpen className="w-6 h-6 text-amber-600" />
                            <h2 className="text-lg font-bold">小游戏乐园 Game</h2>
                        </div>
                        <p className={`mt-2 text-sm ${theme.subText}`}>贪吃蛇、夹娃娃、保龄球与飞行棋。</p>
                        <div className="mt-3 text-xs opacity-60">入口：/game</div>
                    </a>

                    <a href="/ai" className={`group block rounded-xl border ${theme.cardBorder} ${theme.cardBg} p-5 ${theme.hoverBg} transition`}>
                        <div className="flex items-center gap-3">
                            <PenTool className="w-6 h-6 text-purple-600" />
                            <h2 className="text-lg font-bold">AI 工具箱 Toolkit</h2>
                        </div>
                        <p className={`mt-2 text-sm ${theme.subText}`}>精选 AI 生产力工具，探索未来的无限可能。</p>
                        <div className="mt-3 text-xs opacity-60">入口：/ai</div>
                    </a>

                    <a href="/about" className={`group block rounded-xl border ${theme.cardBorder} ${theme.cardBg} p-5 ${theme.hoverBg} transition`}>
                        <div className="flex items-center gap-3">
                            <BookOpen className="w-6 h-6 text-sky-600" />
                            <h2 className="text-lg font-bold">关于 About</h2>
                        </div>
                        <p className={`mt-2 text-sm ${theme.subText}`}>教学成果、教育洞察与联系合作信息。</p>
                        <div className="mt-3 text-xs opacity-60">入口：/about</div>
                    </a>
                </div>
            </main>
        </div>
    )
}
